<template>
  <Card>
    <template #content>
      <div style="position: relative">
        <Loading
          :active="isListLoading && !recommendationsPageLoading"
          :is-full-page="false"
          loader="dots"
          color="#fff"
          background-color="#000"
        />
        <BrBannerMessage v-if="showBannerBRP" />
        <DataTable
          v-model:expanded-rows="expanded"
          :value="recommendationGroups"
          data-key="id"
          responsive-layout="scroll"
          class="recommendations-table"
          :row-class="rowClass"
          :row-style="rowStyle"
          :pt="{
            rowExpansion: {
              class: rowClass
            }
          }"
        >
          <Column
            field="name"
            :header="$t('recommendations.headers.recommendedMeasures')"
            class="recommended-measures-cell"
          >
            <template #body="slotProps">
              <div class="recommended-measure-wrapper">
                <div class="checkbox-wrapper">
                  <Checkbox
                    v-model="slotProps.data.isSelected"
                    :binary="true"
                    @click="toggleGroupSelected(slotProps.data.id)"
                  />

                  <div
                    class="recommendations-icon"
                    :class="getIconName(slotProps.data.name)"
                  >
                    <GammaIcon
                      :src="getIcon(slotProps.data.name)"
                      :default-src="getDefaultIcon(slotProps.data.name)"
                    />
                  </div>
                </div>
                <div class="main-measure">
                  <div class="recommended-measure">
                    <div class="checkbox-label">
                      <div class="recommendation-title">
                        {{ slotProps.data.name }}
                        <span
                          v-if="slotProps.data.detailDescription"
                          class="info pi pi-chevron-circle-down"
                          :class="
                            isExpandedInfo(slotProps.data) ? 'expanded' : ''
                          "
                          @click="() => infoClicked(slotProps.data)"
                        />
                        <span
                          v-if="hasGrantMessage(slotProps.data)"
                          class="info pi pi-info-circle"
                          @click="() => showGrantDialog()"
                        />
                      </div>
                      <div class="subtitle">
                        {{ slotProps.data.description }}
                      </div>
                    </div>
                  </div>
                  <div class="mobile-measure">
                    <RecommendationRating
                      :rating="getSavings(slotProps.data.recommendations)"
                      icon-type="savings"
                    />
                    <RecommendationRating
                      :rating="getComfort(slotProps.data.recommendations)"
                      icon-type="comfort"
                    />
                  </div>
                </div>
              </div>
            </template>
          </Column>
          <Column
            :header="$t('recommendations.headers.savings')"
            header-style="text-align:center"
            body-style="text-align:center"
            class="savings-col"
          >
            <template #body="slotProps">
              <RecommendationRating
                :rating="getSavings(slotProps.data.recommendations)"
                icon-type="savings"
              />
            </template>
          </Column>
          <Column
            :header="$t('recommendations.headers.comfort')"
            body-style="text-align:center"
            class="comfort-col"
          >
            <template #body="slotProps">
              <RecommendationRating
                :rating="getComfort(slotProps.data.recommendations)"
                icon-type="comfort"
              />
            </template>
          </Column>
          <Column
            :header="$t('recommendations.headers.costs')"
            class="estimated-costs-col"
            header-style="text-align:center"
          >
            <template #body="slotProps">
              <div class="estimated-costs">
                <div class="amount-value m">
                  {{ formatNumberToString(getPriceForGroup(slotProps.data)) }}
                </div>
              </div>
            </template>
          </Column>
          <Column
            :expander="true"
            :header="$t('recommendations.headers.details')"
            class="details"
            header-style="text-align:center"
          >
            <template #body="slotProps">
              <div @click="expanderClicked(slotProps.data)">
                <button
                  v-if="isExpandedGroup(slotProps.data)"
                  class="p-row-toggler p-link"
                  type="button"
                  ariaLabel="toggle details"
                >
                  <span class="p-row-toggler-icon pi pi-chevron-down" />
                </button>
                <button
                  v-else
                  class="p-row-toggler p-link"
                  type="button"
                  ariaLabel="toggle details"
                >
                  <span class="p-row-toggler-icon pi pi-chevron-right" />
                </button>
              </div>
            </template>
          </Column>
          <template #expansion="slotProps">
            <RecommendationGroupDetails
              :group-recommendation="slotProps.data"
            />
          </template>
        </DataTable>
      </div>
    </template>
  </Card>
</template>
<script lang="ts">
import {computed, defineComponent} from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import Card from "primevue/card";
import RecommendationRating from "./RecommendationRating.vue";
import RecommendationGroupDetails from "./RecommendationGroupDetails.vue";
import GammaIcon from "@/components/icons/GammaIcon.vue";
import { useRecommendations } from "@/store/recommendations";
import { RecommendationGroup } from "@/types/recommendations";
import {
  emit,
  formatNumberToString,
  getComfort,
  getDefaultIcon,
  getIcon,
  getIconName, getPriceForGroup,
  getSavings,
  LayoutEvent,
} from "@/helpers";
import { storeToRefs } from "pinia";
import Loading from "vue-loading-overlay";
import { onMounted } from "vue";
import {usePassport, useSettings} from "@/store";
import {MenuDisplayOption} from "@/types";
import BrBannerMessage from "@/components/result/br-passport/BrBannerMessage.vue"

import { useRouter } from "vue-router";

export default defineComponent({
  name: "RecommendationListCard",
  components: {
    DataTable,
    Column,
    Checkbox,
    Card,
    RecommendationRating,
    RecommendationGroupDetails,
    Loading,
    GammaIcon,
    BrBannerMessage,
  },
  setup() {
    const recommendationsStore = useRecommendations();
    const {
      toggleGroupSelected,
      updateRecommendationUpgraded,
      expandRecommendation,
      expandInfo,
      isExpandedGroup,
      isExpandedInfo,
    } = recommendationsStore;
    const {
      recommendationGroups,
      isListLoading,
      recommendationsPageLoading,
      expandedRows,
      expanded,
      report,
    } = storeToRefs(recommendationsStore);
    const { steps } = storeToRefs(usePassport());

    const showBannerBRP = computed(() => {
      const md = useSettings().settings?.recommendationsPageOptions?.menuDisplay
      const started = usePassport().steps.length !== 0
      return (report.value && md && Array.isArray(md) && md.find((i) => i === MenuDisplayOption.BRPassport)) && !started
    })
    const router = useRouter();

    function expanderClicked(group: RecommendationGroup) {
      expandRecommendation(group, !isExpandedGroup(group));
    }

    function infoClicked(group: RecommendationGroup) {
      expandInfo(group, !isExpandedInfo(group));
    }

    function getIsSelectedGroup(group: RecommendationGroup) {
      return group.recommendations.find((recom) => recom.isSelected);
    }

    function recommendationIsDisabled(groupId: string) {
      const group = recommendationGroups.value.find((g) => g.id === groupId);
      return group ? !group.isSelected : true;
    }

    function hasGrantMessage(recom: RecommendationGroup) {
      let hasMsg = undefined;
      if (report.value?.grantMessages)
        hasMsg = report.value?.grantMessages.find(
          (msg: any) =>
            msg.recommendationId != undefined &&
            (msg.recommendationId === recom.id ||
              msg.recommendationId ===
                recom.recommendations?.find(
                  (recommendation: any) =>
                    recommendation.id === msg.recommendationId
                )?.id)
        );
      return hasMsg;
    }

    function showGrantDialog() {
      emit(LayoutEvent.ShowGrantDialog, true);
    }

    onMounted(() => {
      expanded.value.forEach((g) => {
        // Fix: Force opening expanded rows
        expanderClicked(g);
        expanderClicked(g);
      });
    });
    function getStepForGroup(group: RecommendationGroup) {
      return steps.value.find(step =>
          step.items.some(item =>
              group.recommendations?.some(r =>
                  item.recommendations?.includes(r.id)
              )
          )
      )
    }

    function rowClass(rowData: RecommendationGroup) {
      return { 'brp-row': !!getStepForGroup(rowData) }
    }

    function rowStyle(rowData: RecommendationGroup) {
      const step = getStepForGroup(rowData)
      if (!step) return

      return {
        '--brp-row': `"Step ${step.step} · ${step.year}"`
      }
    }

    return {
      recommendationGroups,
      expandedRows,
      getIsSelectedGroup,
      updateRecommendationUpgraded,
      toggleGroupSelected,
      recommendationIsDisabled,
      getPriceForGroup,
      getSavings,
      getComfort,
      formatNumberToString,
      isListLoading,
      recommendationsPageLoading,
      expandInfo,
      expanded,
      infoClicked,
      expanderClicked,
      isExpandedGroup,
      isExpandedInfo,
      hasGrantMessage,
      showGrantDialog,
      showBannerBRP,
      rowClass,
      rowStyle,
    };
  },
  methods: { getDefaultIcon, getIconName, getIcon },
});
</script>
<style scoped lang="scss">
.recommended-measure {
  cursor: default;
  display: flex;

  .checkbox-label {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.checkbox-wrapper {
  display: flex;
  align-items: center;

  & > * {
    margin: 0.5rem;
  }

  :deep(img) {
    max-height: 3.5rem;
    max-width: 3.5rem;
  }
}

.savings,
.comfort {
  display: flex;

  svg {
    width: 1.2rem;
    margin: 1px;
  }
}

.savings-detail,
.comfort-detail {
  display: flex;

  svg {
    width: 1.2rem;
    margin: 1px;
  }
}

.info.pi {
  cursor: pointer;
}

.p-card {
  :deep(.p-card-body),
  :deep(.p-card-content) {
    padding: 0;
  }
}

:deep(.p-datatable) {
  .estimated-costs {
    cursor: default;
    width: auto;
    text-align: center;
  }

  .details {
    width: 7rem;
    min-width: 2rem;
    text-align: center;
  }

  .p-datatable-row-expansion {
    td {
      padding: none;
    }
  }
}

.checkbox-wrapper {
  margin-right: 0.5rem;
  display: flex;
  align-items: center;

  :deep(.p-checkbox) {
    margin: 0.5rem;
  }

  .recommendations-icon {
    margin: 0px 0.5rem;
    width: 4rem;
    display: flex;
    align-items: center;

    :deep(svg) {
      width: 100%;
      padding: 0.25rem;
    }
  }
}

.recommendation-title {
  font-weight: bold;
}

@media screen and (min-width: 1420px) {
  .estimated-costs {
    width: 12rem !important;
  }
}

@media screen and (max-width: 1200px) {
  :deep(.p-datatable) {
    .estimated-costs {
      width: 6rem;
    }

    .checkbox-wrapper {
      margin: 0;
    }
  }
}

.estimated-costs-mobile {
  display: none;
  justify-content: center;
  align-items: center;
}

.recommended-measure-wrapper {
  display: flex;
  align-items: center;
}

.recommended-measure .recommendation-title .pi {
  margin-right: 0.25rem;
}

@media screen and (max-width: 800px) {
  .estimated-costs-mobile {
    display: flex;
  }
  :deep(.comfort-col),
  :deep(.savings-col) {
    display: none;
  }
  :deep(.recommendations-table .p-datatable-thead) {
    display: none;
  }
  :deep(.p-datatable) {
    .estimated-costs,
    .details {
      width: auto;
    }
  }
}
:deep(.p-message) {
  margin: 0;
}
</style>
